import type { ProcessingJob } from "features/dt-plus-setup/model/types";

import { refreshProcessingJobs } from "features/dt-plus-setup/api/processingJobs";
import { getErrorMessage } from "features/shared/lib/axios";
import { TR_SCENARIO_CACHE_KEYS } from "features/technical-data/scenario/api/cacheKeys";
import { createTrConfig, patchTrScenario } from "features/technical-data/scenario/api/callers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import type { UseTransientRequestFormReturn } from "./useTransientRequestForm";

import { parseTrConfigId, submitTransientJob } from "../api/callers";
import { buildScenarioDefinitionPayload } from "../model/transientScenario";

type UseTriggerTransientJobParams = {
  onJobStarted: (job: ProcessingJob) => void;
  onSuccess: () => void;
  requestForm: UseTransientRequestFormReturn;
};

export const useTriggerTransientJob = ({ onJobStarted, onSuccess, requestForm }: UseTriggerTransientJobParams) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const [isTriggering, setIsTriggering] = useState(false);

  const trigger = async () => {
    if (!projectId) return;
    const payload = requestForm.getPayload();
    if (payload.scenarios.length === 0) {
      toast.error(t("Execution.transient.configuration.selectAtLeastOne"));
      return;
    }
    try {
      setIsTriggering(true);
      await Promise.all(
        requestForm.scenarios.scenarios.map((scenario) =>
          patchTrScenario(projectId, scenario.id, buildScenarioDefinitionPayload(scenario))
        )
      );
      const configId = parseTrConfigId(await createTrConfig(projectId, payload));
      const job = await submitTransientJob({ configId, projectId });
      await Promise.all([
        queryClient.invalidateQueries(TR_SCENARIO_CACHE_KEYS.list(projectId)),
        refreshProcessingJobs(queryClient, projectId, job),
      ]);
      toast.success(t("Execution.transient.actions.triggerSuccess"));
      onSuccess();
      onJobStarted(job);
    } catch (error) {
      toast.error(getErrorMessage(error, t("Execution.transient.actions.triggerFailed")));
    } finally {
      setIsTriggering(false);
    }
  };

  return { isTriggering, trigger };
};
